/**
 * VoyageDocument.js
 *
 * THE main component for V2 Unified Voyage Log.
 *
 * This is ONE scrolling document for the entire voyage.
 * It replaces all card-based approaches.
 *
 * Structure:
 * - MobileVoyageHeader (mobile only - floating dark header from top)
 * - VoyageHeader (tablet/desktop - sticky within content)
 * - List of LogEntry components (one for each entry in unifiedLog)
 * - FloatingActionBar (fixed at bottom - 4 buttons on mobile)
 * - Modals for skip and operation data entry
 *
 * PHASE 3 v2: Mobile full-screen mode with:
 * - Dark floating header at top of screen
 * - Simplified 4-button action bar (Back, Log, Operation, Menu)
 * - Log picker slide-up modal
 */

import React, { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import { Search, X, CheckCircle } from 'lucide-react';
import VoyageHeader from './VoyageHeader';
import MobileVoyageHeader from './MobileVoyageHeader';
import LogEntry from './LogEntry';
import FloatingActionBar from './FloatingActionBar';
import SkipCheckModal from './SkipCheckModal';
import OperationDataModal from './OperationDataModal';
import NoteInputModal from './NoteInputModal';
import WeatherInputModal from './WeatherInputModal';
import FuelInputModal from './FuelInputModal';
import PositionInputModal from './PositionInputModal';
import CompassCheckModal from './CompassCheckModal';
import OperationPickerModal from './OperationPickerModal';
import RestrictedVisibilityPrompt from './RestrictedVisibilityPrompt';
import DailyChecksQuickAccess from './DailyChecksQuickAccess';
import ConfirmSignOffModal from '../../../../shared/ConfirmSignOffModal';  // FT-202: ALL FAST modal
import {
  CHECK_IMMUTABILITY_WINDOW_MS,
  VOYAGE_STATUS
} from '../../../../../config/operationsConfig';
// FT-161 Phase 2: Compact pre-departure card
// FT-161 Phase 4: Operation cards
import { PreDepartureCompactCard, OperationCard } from '../VoyageLogCards';

// Pass 2o: ad-hoc activity window. Triplicated predicate (anti-Pass-2b — do
// NOT extract). Mirrored in UnderwayPhase + VoyagePlanUnderwayCard.
const ACTIVE_OPERATION_ADHOC_WINDOW_MS = 60 * 60 * 1000;  // 60 minutes
// eslint-disable-next-line complexity
function findActiveSection(log, planItems, nowMs) {
  const entries = log || [];
  const sections = entries.filter(e => e && e.type === 'section' && e.operationId);
  const completedIds = new Set(
    entries.filter(e => e && e.type === 'operation_complete' && e.operationId).map(e => e.operationId)
  );
  const items = planItems || {};
  for (const section of [...sections].reverse()) {
    if (completedIds.has(section.operationId)) continue;
    if (section.planItemId) {
      const itemStatus = items[section.planItemId]?.status;
      if (itemStatus && itemStatus !== 'completed' && itemStatus !== 'skipped') return section;
      if (itemStatus === 'completed' || itemStatus === 'skipped') continue;
      // itemStatus undefined: fall through to ad-hoc window
    }
    const lastActivityMs = entries.reduce((max, e) => {
      if (!e || e.type !== 'check' || e.operationId !== section.operationId) return max;
      const t = e.completedAt || e.skippedAt || null;
      if (!t) return max;
      const ms = new Date(t).getTime();
      return Number.isFinite(ms) && ms > max ? ms : max;
    }, 0);
    if (lastActivityMs > 0 && nowMs - lastActivityMs <= ACTIVE_OPERATION_ADHOC_WINDOW_MS) return section;
  }
  return null;
}

/**
 * FT-283 §5.1 / §7: green "Complete operation" row at the bottom of every
 * active operation's checklist. Hosts an <InlineTimeField> so the user can
 * single-tap (default Now / pre-arrival last-check), or expand to backdate.
 *
 * Props:
 *   - activeOperation:    section entry for the in-flight operation
 *   - operationChecks:    array of check entries for this operationId
 *   - voyage, currentVessel: timezone fallback chain + clock-skew clamp
 *   - onRequestComplete:  parent handler — receives (eventTime, clockSkew)
 *
 * Rules-of-Hooks safe: useInlineEventTime is called UNCONDITIONALLY at the
 * top. The component only mounts when the parent decides to render it
 * (gated on activeOperation + voyage.status + !hasEnded).
 */
const CompleteOperationRow = ({
  activeOperation,
  operationChecks,
  voyage,
  currentVessel,
  onRequestComplete
}) => {
  const vesselTimezone = voyage?.preferredTimezone
    || currentVessel?.settings?.timezone
    || currentVessel?.timezone
    || 'UTC';

  const timeField = useInlineEventTime(vesselTimezone);
  const submitInProgressRef = useRef(false);

  // FT-283 §7.2: pre-arrival path computes lastCheckCompletedAt (max of
  // completedAt / skippedAt across all checks for this operation), clamped
  // to [departureTime, Now]. clockSkew === true when clamping fires.
  const isPreArrival = activeOperation?.operationType === 'pre_arrival';

  const { lastCheckMs, clockSkew: skewFromClamp } = useMemo(() => {
    if (!isPreArrival) {
      return { lastCheckMs: null, clockSkew: false };
    }
    const stamps = (operationChecks || [])
      .map(c => c.completedAt || c.skippedAt)
      .filter(Boolean)
      .map(s => new Date(s).getTime())
      .filter(Number.isFinite);
    let ms = stamps.length ? Math.max(...stamps) : Date.now();
    const departureMs = voyage?.departureTime ? new Date(voyage.departureTime).getTime() : null;
    const nowMs = Date.now();
    let clockSkew = false;
    if (departureMs !== null && Number.isFinite(departureMs) && ms < departureMs) {
      ms = departureMs;
      clockSkew = true;
    }
    if (ms > nowMs) {
      ms = nowMs;
      clockSkew = true;
    }
    return { lastCheckMs: ms, clockSkew };
  }, [isPreArrival, operationChecks, voyage?.departureTime]);

  // Resolve "default" time displayed on the badge.
  //   Mid-voyage:   InlineTimeField default — fresh Now from the hook.
  //   Pre-arrival:  override badge label + use the clamped last-check time.
  // The inline field's `freshSnapshot()` always returns Now when state==='now'
  // for mid-voyage. For pre-arrival, we don't use freshSnapshot's 'now' path;
  // we pass the clamped last-check time straight through to the parent.

  // FT-283: boundary-error banner state. Parent's onRequestComplete returns
  // { boundaryError } on bounds failure so the row stays visible and the user
  // can re-edit (or pick a >4h override reason via the inline field).
  const [boundaryError, setBoundaryError] = useState(null);

  const handleComplete = useCallback(async () => {
    if (submitInProgressRef.current) return;
    if (!timeField.isValid) return;
    submitInProgressRef.current = true;
    setBoundaryError(null);
    try {
      let resolvedEventTime;
      let resolvedClockSkew = false;
      let delayReason;
      if (timeField.state === 'editing') {
        // User explicitly backdated — use the snapshot.
        const snap = timeField.freshSnapshot();
        resolvedEventTime = snap.eventTime;
        delayReason = snap.delayReason;
      } else if (isPreArrival) {
        // Pre-arrival default — clamped last-check time.
        resolvedEventTime = new Date(lastCheckMs).toISOString();
        resolvedClockSkew = skewFromClamp;
      } else {
        // Mid-voyage default — Now from a fresh snapshot.
        const snap = timeField.freshSnapshot();
        resolvedEventTime = snap.eventTime;
        delayReason = snap.delayReason;
      }
      const result = await onRequestComplete?.(resolvedEventTime, resolvedClockSkew, delayReason);
      if (result?.boundaryError) {
        setBoundaryError(result.boundaryError);
        return;
      }
    } finally {
      submitInProgressRef.current = false;
    }
  }, [timeField, isPreArrival, lastCheckMs, skewFromClamp, onRequestComplete]);

  return (
    <div
      data-operation-id={activeOperation?.operationId}
      data-testid="complete-operation-row"
      className="p-4 bg-green-50 border-t-2 border-green-200 space-y-3"
    >
      {boundaryError && (
        <ModalErrorBanner message={boundaryError.humanMessage} />
      )}
      <InlineTimeField
        timeField={timeField}
        vesselTimezone={vesselTimezone}
        nowBadgeLabel={isPreArrival ? 'Last check' : 'Now'}
        label="Event time"
      />
      <button
        type="button"
        onClick={handleComplete}
        disabled={!timeField.isValid || submitInProgressRef.current}
        className="w-full py-4 min-h-[56px] bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
      >
        <CheckCircle className="h-5 w-5" />
        <span>Complete operation</span>
      </button>
    </div>
  );
};
// FT-283: vessel context for timezone fallback chain on the Complete button.
import { useVessel } from '../../../../../contexts/VesselContext';
// FT-283: unified Complete operation flow.
import BulkSkipReasonModal from './BulkSkipReasonModal';
import InlineTimeField from './InlineTimeField';
import ModalErrorBanner from './ModalErrorBanner';
import { useInlineEventTime } from './useInlineEventTime';

const VoyageDocument = ({
  voyage,
  onEditVoyage,
  onCompleteCheck,
  onSkipCheck,
  onLogPosition,
  onLogWeather,
  onLogNote,
  onLogFuel,  // Handler for fuel reading
  onStartOperation,
  onCompleteOperation,  // FT-283: prefilled-options aware; called for the no-skip path
  onBulkSkipAndComplete,  // FT-283: atomic skip + complete (passed in from useVoyages)
  onPrepareForArrival,  // Handler to trigger arrival flow
  onClose,  // Handler to close/minimize voyage view
  showAllFastModal,  // FT-202: ALL FAST modal state
  onAllFast,  // FT-202: Show ALL FAST modal
  onAllFastConfirm,  // FT-202: Confirm ALL FAST
  onAllFastCancel,  // FT-202: Cancel ALL FAST
  allFastTimeField,  // FT-285 Item 2: inline event-time hook for All Fast sign-off
  vesselTimezone,  // FT-284: threaded down to in-voyage modals' InlineTimeField
  // Sub-ticket C: optional top-of-scroll slot for the VoyagePlanUnderwayCard.
  // Owned by UnderwayPhase; rendered at the top of the scrollable content area
  // so the card lives ABOVE the log entries and the FAB. Defaults to null
  // (PreDeparturePhase / PreArrivalPhase do not pass a slot).
  topSlot = null,
  // Sub-ticket C: plan-card-tap contexts. When set (non-null), the relevant
  // data-capture modal is force-opened directly (bypassing OperationPicker
  // / FAB) so the Master enters straight into the tapped item's flow. Cleared
  // by the modal's submit/cancel callback via the onClear* handlers.
  planItemOperationContext = null,
  planItemPositionContext = null,
  onClearPlanItemOperationContext,
  onClearPlanItemPositionContext
}) => {
  // FT-283: vessel for tz fallback chain — voyage.preferredTimezone takes
  // precedence; fallback is handled inside CompleteOperationRow.
  const { currentVessel } = useVessel();
  // Modal state
  const [skipModalData, setSkipModalData] = useState(null);
  const [operationDataModal, setOperationDataModal] = useState(null);
  // FT-281: pendingCompletion state + auto-detect effect + green modal deleted.
  // TimePickerModal in UnderwayPhase is the canonical user-visible confirm.
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [annotationTarget, setAnnotationTarget] = useState(null);  // Entry being annotated
  const [showWeatherModal, setShowWeatherModal] = useState(false);
  const [showFuelModal, setShowFuelModal] = useState(false);
  const [showPositionModal, setShowPositionModal] = useState(false);
  const [showCompassModal, setShowCompassModal] = useState(false);
  const [showOperationPicker, setShowOperationPicker] = useState(false);
  const [showDailyChecks, setShowDailyChecks] = useState(false);  // FT-80: Daily Checks quick-access

  // FT-283: bulk-skip-reason modal mount state + pending payload to thread
  // through to the bulk hook after user confirms a reason.
  const [bulkSkipReasonOpen, setBulkSkipReasonOpen] = useState(false);
  const [bulkSkipPayload, setBulkSkipPayload] = useState(null);
  // FT-283: in-flight guard for the Complete operation flow. Spans
  // BulkSkipReasonModal → bulkSkipAndComplete to prevent double-taps from
  // firing two operation_complete writes.
  const completeInProgressRef = useRef(false);

  // Restricted visibility prompt state
  const [restrictedVisPrompt, setRestrictedVisPrompt] = useState({
    isOpen: false,
    visibility: null
  });
  const [dismissedRestrictedVis, setDismissedRestrictedVis] = useState(false);

  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');

  // ─────────────────────────────────────────────────────────────────────────
  // Sub-ticket C: refs mirror the plan-item context props for closure-stable
  // async reads from handlers (handleCompleteOperationRequest /
  // handlePositionSubmit run in async closures captured at modal-mount time).
  // ─────────────────────────────────────────────────────────────────────────
  const planItemOperationContextRef = useRef(null);
  const planItemPositionContextRef = useRef(null);
  useEffect(() => { planItemOperationContextRef.current = planItemOperationContext; }, [planItemOperationContext]);
  useEffect(() => { planItemPositionContextRef.current = planItemPositionContext; }, [planItemPositionContext]);

  // Get the unified log array (default to empty array)
  const unifiedLog = voyage?.unifiedLog || [];

  // ═══════════════════════════════════════════════════════════════
  // COMPUTED STATE
  // ═══════════════════════════════════════════════════════════════

  // Check if voyage has started (LET GO milestone exists)
  const hasStarted = useMemo(() => {
    return unifiedLog.some(
      entry => entry.type === 'milestone' && entry.milestoneType === 'let_go'
    );
  }, [unifiedLog]);

  // Check if voyage has ended (ALL FAST milestone exists)
  const hasEnded = useMemo(() => {
    return unifiedLog.some(
      entry => entry.type === 'milestone' && entry.milestoneType === 'all_fast'
    );
  }, [unifiedLog]);

  // Find active operation (started but not completed).
  // [Pass 2n] Pick the LATEST unmatched section, not the first. Voyages that
  // accumulated stale "section" entries from earlier abandoned-operation paths
  // (no operation_complete written) caused this derivation to pin to an
  // ancient operationId, mismatching fresh checks which write against the
  // newest section's id. Symptom: bulk-skip-reason modal fired with stale
  // incomplete checks from a long-finished section.
  //
  // unifiedLog is append-order chronological (see useVoyages.js — all writes
  // go through [...unifiedLog, newEntry] / .push(); no sort/reverse before
  // persist). Last unmatched in array order = chronologically newest.
  //
  // Uses [...arr].reverse().find(...) — findLast-equivalent, matches the
  // existing in-repo portable pattern at VoyagePlanUnderwayCard.js:215-218.
  // [Pass 2o] Self-heal orphan sections: active iff (a) no operation_complete
  // AND (b) non-terminal plan_item OR recent ad-hoc check activity. Predicate
  // body mirrored in UnderwayPhase + VoyagePlanUnderwayCard (anti-Pass-2b).
  const activeOperation = useMemo(() => {
    return findActiveSection(unifiedLog, voyage?.voyagePlanItems || {}, Date.now());
  }, [unifiedLog, voyage?.voyagePlanItems]);

  // Check if all checks for the active operation are completed or skipped
  const allOperationChecksDone = useMemo(() => {
    if (!activeOperation?.operationId) return false;

    // Get all checks for this operation
    const operationChecks = unifiedLog.filter(
      e => e.type === 'check' && e.operationId === activeOperation.operationId
    );

    // If no checks, operation can be completed
    if (operationChecks.length === 0) return true;

    // Check if ALL checks are either completed or skipped
    return operationChecks.every(check => check.completed || check.skipped);
  }, [unifiedLog, activeOperation]);

  // FT-161 Phase 2: Check if ALL pre-departure checks are completed
  const preDepCheckStatus = useMemo(() => {
    // Get all pre-departure check entries from unifiedLog
    const preDepChecks = unifiedLog.filter(
      e => e.type === 'check' && e.checkType === 'pre_departure'
    );

    // Get expected count from voyage.preDepartureChecks or use actual count
    const expectedCount = voyage?.preDepartureChecks?.length || preDepChecks.length;

    // Check if all are completed
    const allCompleted = preDepChecks.length >= expectedCount &&
                         preDepChecks.length > 0 &&
                         preDepChecks.every(c => c.completed);

    return {
      checks: preDepChecks,
      allCompleted,
      expectedCount,
      completedCount: preDepChecks.filter(c => c.completed).length
    };
  }, [unifiedLog, voyage?.preDepartureChecks?.length]);

  // Get current active phase for the operation (for multi-phase operations)
  const currentPhase = useMemo(() => {
    if (!activeOperation?.operationId) return null;

    // Get all checks for this operation
    const operationChecks = unifiedLog.filter(
      e => e.type === 'check' && e.operationId === activeOperation.operationId
    );

    // If no checks have phases, return null (legacy operation)
    if (!operationChecks.some(c => c.phase)) return null;

    // Get unique phases sorted
    const phases = [...new Set(operationChecks.map(c => c.phase).filter(Boolean))].sort();

    // Find first phase with incomplete checks
    for (const phase of phases) {
      const phaseChecks = operationChecks.filter(c => c.phase === phase);
      const allDone = phaseChecks.every(c => c.completed || c.skipped);
      if (!allDone) return phase;
    }

    // All phases complete - return last phase
    return phases[phases.length - 1];
  }, [unifiedLog, activeOperation]);

  // FT-202: Check if pre_arrival operation was just completed (show ALL FAST button)
  const preArrivalJustCompleted = useMemo(() => {
    // Find if there's a completed pre_arrival operation
    const preArrivalSection = unifiedLog.find(
      e => e.type === 'section' && e.operationType === 'pre_arrival' && e.operationId
    );
    if (!preArrivalSection) return false;

    // Check if it has a completion entry
    const hasCompletion = unifiedLog.some(
      e => e.type === 'operation_complete' && e.operationId === preArrivalSection.operationId
    );
    if (!hasCompletion) return false;

    // Check if ALL FAST milestone already exists
    const hasAllFast = unifiedLog.some(
      e => e.type === 'milestone' && e.milestoneType === 'all_fast'
    );

    // Show ALL FAST button if pre_arrival completed but ALL FAST not yet done
    return !hasAllFast;
  }, [unifiedLog]);

  // FT-285 Item 4: hard lock once pre_arrival operation_complete exists. This
  // is distinct from preArrivalJustCompleted (which flips false after ALL FAST
  // fires). preArrivalDone stays permanently true so the Arrive FAB button
  // and LogPickerModal Arrive entry stay disabled — preventing duplicate
  // operation_complete audit rows MCA would interpret as a duplicate
  // state transition.
  const preArrivalDone = useMemo(() => {
    const preArrivalSection = unifiedLog.find(
      e => e.type === 'section' && e.operationType === 'pre_arrival' && e.operationId
    );
    if (!preArrivalSection) return false;
    return unifiedLog.some(
      e => e.type === 'operation_complete' && e.operationId === preArrivalSection.operationId
    );
  }, [unifiedLog]);

  // FT-281: auto-detect "all checks done → prompt to complete" effect removed.
  // The Complete button on the active operation card is the explicit user
  // action; TimePickerModal in UnderwayPhase is the visible confirmation.

  // Sort entries by timestamp for display
  // Use createdAt as fallback for entries without timestamp (uncompleted checks)
  // ANNOTATION STICKY: Annotations (entries with linkedEntryId) stay with their parent entry
  const sortedEntries = useMemo(() => {
    // Separate annotations from regular entries
    const annotations = unifiedLog.filter(e => e.linkedEntryId);
    const regularEntries = unifiedLog.filter(e => !e.linkedEntryId);

    // Sort regular entries by timestamp
    regularEntries.sort((a, b) => {
      const timeA = a.timestamp
        ? new Date(a.timestamp).getTime()
        : (a.createdAt ? new Date(a.createdAt).getTime() : 0);
      const timeB = b.timestamp
        ? new Date(b.timestamp).getTime()
        : (b.createdAt ? new Date(b.createdAt).getTime() : 0);
      return timeA - timeB;
    });

    // Build result array, inserting annotations after their parent entries
    const result = [];
    for (const entry of regularEntries) {
      result.push(entry);
      // Find any annotations linked to this entry and add them immediately after
      const linkedAnnotations = annotations
        .filter(a => a.linkedEntryId === entry.id)
        .sort((a, b) => {
          // Sort annotations by their own timestamp
          const timeA = a.timestamp ? new Date(a.timestamp).getTime() : (a.createdAt ? new Date(a.createdAt).getTime() : 0);
          const timeB = b.timestamp ? new Date(b.timestamp).getTime() : (b.createdAt ? new Date(b.createdAt).getTime() : 0);
          return timeA - timeB;
        });
      result.push(...linkedAnnotations);
    }

    // Add any orphaned annotations (parent entry deleted) at the end
    const usedAnnotationIds = new Set(result.filter(e => e.linkedEntryId).map(e => e.id));
    const orphanedAnnotations = annotations.filter(a => !usedAnnotationIds.has(a.id));
    result.push(...orphanedAnnotations);

    return result;
  }, [unifiedLog]);

  // Filter entries based on search term and type filter
  const filteredEntries = useMemo(() => {
    let filtered = sortedEntries;

    // Apply type filter
    if (typeFilter !== 'all') {
      filtered = filtered.filter(entry => {
        switch (typeFilter) {
          case 'position':
            return entry.type === 'position';
          case 'weather':
            return entry.type === 'weather';
          case 'fuel':
            return entry.type === 'fuel';
          case 'note':
            return entry.type === 'note' && !entry.linkedEntryId; // Exclude annotations
          case 'comms':
            return entry.type === 'comms';
          case 'check':
            return entry.type === 'check';
          case 'milestone':
            return entry.type === 'milestone' || entry.type === 'section' || entry.type === 'operation_complete';
          default:
            return true;
        }
      });
    }

    // Apply search term
    if (searchTerm.trim()) {
      const query = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(entry => {
        // Search in common fields
        const searchableFields = [
          entry.label,
          entry.note,
          entry.notes,
          entry.text,
          entry.message,
          entry.summary,
          entry.description,
          entry.skipReason,
          entry.operationType?.replace(/_/g, ' '),
          entry.milestoneType?.replace(/_/g, ' '),
          // Position fields
          entry.latitude?.toString(),
          entry.longitude?.toString(),
          entry.position,
          // Weather fields
          entry.windDirection,
          entry.seaState,
          entry.visibility,
          // Fuel fields
          entry.fuelReading?.toString(),
          entry.fuelType,
        ].filter(Boolean);

        return searchableFields.some(field =>
          field.toLowerCase().includes(query)
        );
      });
    }

    return filtered;
  }, [sortedEntries, searchTerm, typeFilter]);

  // FT-161 Phase 4: Group COMPLETED operation entries by operationId
  // IMPORTANT: Only group operations that are COMPLETE - in-progress operations
  // use the standard checklist UI flow
  const operationGroups = useMemo(() => {
    const groups = {};
    unifiedLog.forEach(entry => {
      if (entry.operationId && ['section', 'operation_data', 'check', 'operation_complete'].includes(entry.type)) {
        // Skip pre_departure operations - handled separately by PreDepartureCompactCard
        if (entry.operationType === 'pre_departure' || entry.checkType === 'pre_departure') {
          return;
        }
        if (!groups[entry.operationId]) {
          groups[entry.operationId] = { section: null, data: null, checks: [], complete: null };
        }
        if (entry.type === 'section') groups[entry.operationId].section = entry;
        if (entry.type === 'operation_data') groups[entry.operationId].data = entry;
        if (entry.type === 'check') groups[entry.operationId].checks.push(entry);
        if (entry.type === 'operation_complete') groups[entry.operationId].complete = entry;
      }
    });
    // Only return groups that are COMPLETE (have operation_complete entry)
    // In-progress operations should NOT be grouped - they use the standard checklist UI
    const completedGroups = {};
    Object.entries(groups).forEach(([opId, group]) => {
      if (group.complete) {
        completedGroups[opId] = group;
      }
    });
    return completedGroups;
  }, [unifiedLog]);

  // FT-161 Phase 2 & 4: Process entries to group pre-departure checks and operations
  const processedEntries = useMemo(() => {
    let entries = [...filteredEntries];

    // Phase 2: Filter out pre-departure checks when all completed
    if (preDepCheckStatus.allCompleted) {
      entries = entries.filter(
        e => !(e.type === 'check' && e.checkType === 'pre_departure')
      );
    }

    // Phase 4: Replace scattered operation entries with operation_card entries
    // Track which operationIds we've already added a card for
    const processedOperationIds = new Set();
    const result = [];

    entries.forEach(entry => {
      // Skip operation entries that belong to a grouped operation (not pre_departure)
      if (entry.operationId && operationGroups[entry.operationId]) {
        const group = operationGroups[entry.operationId];
        // Only process each operation once - at the section entry position
        if (entry.type === 'section' && !processedOperationIds.has(entry.operationId)) {
          processedOperationIds.add(entry.operationId);
          // Create an operation_card entry
          result.push({
            id: `operation_card_${entry.operationId}`,
            type: 'operation_card',
            operationId: entry.operationId,
            operationType: entry.operationType,
            timestamp: entry.timestamp,
            sectionEntry: group.section,
            operationDataEntry: group.data,
            checks: group.checks,
            completeEntry: group.complete
          });
        }
        // Skip other operation-related entries (operation_data, checks, operation_complete)
        // They're rendered inside the card
        return;
      }
      // Keep all other entries as-is
      result.push(entry);
    });

    return result;
  }, [filteredEntries, preDepCheckStatus.allCompleted, operationGroups]);

  // Type filter options
  const typeFilterOptions = [
    { value: 'all', label: 'All' },
    { value: 'position', label: 'Position' },
    { value: 'weather', label: 'Weather' },
    { value: 'fuel', label: 'Fuel' },
    { value: 'note', label: 'Notes' },
    { value: 'comms', label: 'Comms' },
    { value: 'check', label: 'Checks' },
    { value: 'milestone', label: 'Milestones' },
  ];

  // ═══════════════════════════════════════════════════════════════
  // CHECK MODIFICATION RULES
  // ═══════════════════════════════════════════════════════════════

  /**
   * Determine if a check can still be modified.
   * Rules:
   * - Cannot modify if voyage has ended
   * - Cannot modify if check is already completed/skipped AND more than 5 minutes have passed
   */
  const canModifyCheck = useCallback((entry) => {
    // Cannot modify anything after voyage ends
    if (hasEnded) return false;

    // If not completed or skipped, can always modify
    if (!entry.completed && !entry.skipped) return true;

    // If completed/skipped, check 5-minute window
    if (!entry.timestamp) return true;

    const entryTime = new Date(entry.timestamp).getTime();
    const now = Date.now();
    const window = CHECK_IMMUTABILITY_WINDOW_MS || (5 * 60 * 1000);

    return (now - entryTime) < window;
  }, [hasEnded]);

  // ═══════════════════════════════════════════════════════════════
  // EVENT HANDLERS
  // ═══════════════════════════════════════════════════════════════

  // Handle check completion
  const handleCheckComplete = useCallback((entry) => {
    onCompleteCheck?.(entry);
  }, [onCompleteCheck]);

  // Handle skip request (opens modal)
  const handleCheckSkipRequest = useCallback((entry) => {
    setSkipModalData(entry);
  }, []);

  // Handle skip confirm (from modal)
  const handleSkipConfirm = useCallback((reason, notes) => {
    if (skipModalData) {
      onSkipCheck?.(skipModalData, reason, notes);
      setSkipModalData(null);
    }
  }, [skipModalData, onSkipCheck]);

  // Handle skip cancel
  const handleSkipCancel = useCallback(() => {
    setSkipModalData(null);
  }, []);

  // ─────────────────────────────────────────────────────────────────────────
  // Sub-ticket C: when UnderwayPhase sets planItemOperationContext (a
  // VoyagePlanUnderwayCard operation-ref tap), force-open OperationDataModal
  // directly with the planned operationType — bypassing OperationPickerModal
  // which is the FAB path. We DO NOT re-open the modal if it's already open
  // (e.g. user already tapped, then context fires a re-render) — checking
  // `operationDataModal === null` ensures we open exactly once per context.
  // The submit / cancel handlers below call onClearPlanItemOperationContext
  // to flip context to null, closing the loop.
  // ─────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (planItemOperationContext && !operationDataModal) {
      setOperationDataModal({ operationType: planItemOperationContext.operationType });
      // Defensive: also close the picker if it somehow ended up open.
      setShowOperationPicker(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planItemOperationContext]);

  // ─────────────────────────────────────────────────────────────────────────
  // Sub-ticket C: same pattern for waypoint taps → PositionInputModal.
  // PositionInputModal's contract is intentionally UNCHANGED per
  // CODING-BRIEF-C §6.3 LOCKED — the planItemId plumbing lives in
  // UnderwayPhase's handleLogPosition via its planItemPositionContextRef.
  // We only force-open the modal here; the planItemId flows through the
  // ref-read side channel.
  // ─────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (planItemPositionContext && !showPositionModal) {
      setShowPositionModal(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planItemPositionContext]);

  // Handle operation picker open
  const handleOperationPickerRequest = useCallback(() => {
    setShowOperationPicker(true);
  }, []);

  // Handle operation type selection (from picker modal)
  const handleOperationSelect = useCallback((operationType) => {
    setShowOperationPicker(false);
    // Open the data modal for the selected operation type
    setOperationDataModal({ operationType });
  }, []);

  // Handle operation data submit (from modal)
  // FT-284: forward timeOptions; close-on-success owned by parent here. Modal
  // surfaces boundary error via its local <ModalErrorBanner>, so we only
  // dismiss the modal when no boundary error is returned.
  // [Pass 1 FIX R1-C1 — Option B] Context lifetime extended through Complete.
  // Cleared in handleCompleteOperationRequest / handleBulkSkipConfirm success
  // branches AND defensively in UnderwayPhase's activeOperation-mismatch
  // effect. The previous on-START clear nulled the ref before the Complete
  // handler could read it, leaving plan-item rows stuck at in_progress.
  const handleOperationDataSubmit = useCallback(async (operationType, data, timeOptions) => {
    // Pass 2o: thread planItemId through for plan-tied ops; FAB / ad-hoc
    // legitimately leave it undefined.
    const planItemId = planItemOperationContextRef.current?.planItemId || null;
    const startOptions = planItemId ? { ...(timeOptions || {}), planItemId } : timeOptions;
    const result = await onStartOperation?.(operationType, data, startOptions);
    if (result?.boundaryError) {
      return result;
    }
    setOperationDataModal(null);
  }, [onStartOperation]);

  // Handle operation data cancel
  // Sub-ticket C: also clear the plan-item context so the next FAB Op tap
  // (or another card tap) starts from a clean slate.
  const handleOperationDataCancel = useCallback(() => {
    setOperationDataModal(null);
    onClearPlanItemOperationContext?.();
  }, [onClearPlanItemOperationContext]);

  // FT-281: handleCompleteOperationConfirm / handleCompleteOperationCancel
  // deleted along with the green auto-confirm modal that referenced them.
  // FT-283: handleDirectCompleteOperation deleted. The FAB Complete button is
  // gone — Complete is now owned by <CompleteOperationRow> at the bottom of
  // the active operation's checklist. See handleCompleteOperationRequest below.

  // FT-283 §4: orchestrator for the "Complete operation" tap.
  //
  // Called from <CompleteOperationRow> with the resolved event time + a
  // clockSkew flag (pre-arrival path clamps the last-check time to the
  // [departureTime, Now] window; clockSkew === true when the clamp fired).
  //
  // Flow:
  //   1. Idempotency guard — silently bail if an operation_complete entry
  //      already exists for this operationId (sync race).
  //   2. In-flight guard — completeInProgressRef.current.
  //   3. Branch on incomplete-check count:
  //        > 0 → open BulkSkipReasonModal; stash payload; user-confirm
  //              triggers handleBulkSkipConfirm → onBulkSkipAndComplete().
  //       == 0 → fire onCompleteOperation(operationId, prefilledOptions)
  //              directly. UnderwayPhase strips prefilled/clockSkew at the
  //              boundary so they never reach addUnifiedLogEntry.
  const handleCompleteOperationRequest = useCallback(async (resolvedEventTime, clockSkew, delayReason) => {
    if (!activeOperation?.operationId) return { boundaryError: null };
    const operationId = activeOperation.operationId;

    // Idempotency: if operation_complete already lives in the log, bail.
    const alreadyComplete = unifiedLog.some(
      e => e.type === 'operation_complete' && e.operationId === operationId
    );
    if (alreadyComplete) return { boundaryError: null };

    if (completeInProgressRef.current) return { boundaryError: null };
    completeInProgressRef.current = true;

    try {
      const operationChecks = unifiedLog.filter(
        e => e.type === 'check' && e.operationId === operationId
      );
      const incomplete = operationChecks.filter(c => !c.completed && !c.skipped);

      if (incomplete.length > 0) {
        // Bulk-skip path: stash payload (incl. delayReason), open modal.
        // completeInProgressRef stays TRUE until the modal resolves
        // (handleBulkSkipConfirm or handleBulkSkipCancel both release it).
        setBulkSkipPayload({
          operationId,
          incomplete,
          resolvedEventTime,
          clockSkew,
          delayReason
        });
        setBulkSkipReasonOpen(true);
        // NOTE: do not release the in-flight guard here — the modal owns it.
        return { boundaryError: null };
      }

      // No-skip path: fire onCompleteOperation with prefilled options.
      // UnderwayPhase strips prefilled/clockSkew at its boundary; delayReason
      // is in ...rest and rides through to completeOperationV2.
      // [Pass 1 FIX R1-C1] Thread planItemId from ref so the operation_complete
      // entry carries the plan tie. Clear context on success (was at line 721).
      try {
        const planItemId = planItemOperationContextRef.current?.planItemId || null;
        const result = await onCompleteOperation?.(operationId, {
          prefilled: true,
          eventTime: resolvedEventTime,
          clockSkew,
          ...(delayReason ? { delayReason } : {}),
          ...(planItemId ? { planItemId } : {})
        });
        if (result?.boundaryError) {
          return { boundaryError: result.boundaryError };
        }
        onClearPlanItemOperationContext?.();
        return { boundaryError: null };
      } finally {
        completeInProgressRef.current = false;
      }
    } catch (err) {
      // Hooks return null on failure rather than throwing, but be defensive.
      console.warn('[VoyageDocument] handleCompleteOperationRequest error (non-fatal):', err);
      completeInProgressRef.current = false;
      return { boundaryError: null };
    }
  }, [activeOperation, unifiedLog, onCompleteOperation, onClearPlanItemOperationContext]);

  // FT-283: BulkSkipReasonModal confirm → fire atomic bulkSkipAndComplete.
  const handleBulkSkipConfirm = useCallback(async (bulkReason) => {
    if (!bulkSkipPayload || !voyage?.id) {
      setBulkSkipReasonOpen(false);
      setBulkSkipPayload(null);
      completeInProgressRef.current = false;
      return;
    }
    const { operationId, incomplete, resolvedEventTime, clockSkew } = bulkSkipPayload;

    // Resolve the timezone fallback chain (voyage-first).
    const eventTimezone = voyage.preferredTimezone
      || currentVessel?.settings?.timezone
      || currentVessel?.timezone
      || 'UTC';

    const networkStatusAtRecord = (typeof navigator !== 'undefined' && navigator.onLine === false)
      ? 'offline'
      : null;

    const skipItems = incomplete.map(c => ({
      checkEntryId: c.id,
      reason: bulkReason,
      notes: ''
    }));

    // [Pass 1 FIX R1-C1] Thread planItemId from ref so the bulk-skip
    // operation_complete entry carries the plan tie. Clear context on success.
    const planItemId = planItemOperationContextRef.current?.planItemId || null;
    try {
      await onBulkSkipAndComplete?.(
        voyage.id,
        skipItems,
        {
          eventTime: resolvedEventTime,
          eventTimezone,
          recordedAtDevice: new Date().toISOString(),
          operationId,
          bulkSkipReason: bulkReason,
          skippedCheckIds: incomplete.map(c => c.id),
          ...(clockSkew ? { clockSkew: true } : {}),
          ...(networkStatusAtRecord ? { auditContext: { networkStatusAtRecord } } : {}),
          ...(planItemId ? { planItemId } : {})
        }
      );
      onClearPlanItemOperationContext?.();
    } catch (err) {
      console.warn('[VoyageDocument] bulkSkipAndComplete error (non-fatal):', err);
    } finally {
      setBulkSkipReasonOpen(false);
      setBulkSkipPayload(null);
      completeInProgressRef.current = false;
    }
  }, [bulkSkipPayload, voyage, currentVessel, onBulkSkipAndComplete, onClearPlanItemOperationContext]);

  // FT-283: BulkSkipReasonModal cancel → release in-flight, no state change.
  // Pass 1 symmetry fix: also clear planItemOperationContext to match
  // handleOperationDataCancel:727-730. Clear is idempotent on null.
  const handleBulkSkipCancel = useCallback(() => {
    setBulkSkipReasonOpen(false);
    setBulkSkipPayload(null);
    completeInProgressRef.current = false;
    onClearPlanItemOperationContext?.();
  }, [onClearPlanItemOperationContext]);

  // Handle note modal open (standalone note)
  const handleNoteRequest = useCallback(() => {
    setAnnotationTarget(null);  // No linked entry
    setShowNoteModal(true);
  }, []);

  // Handle annotation request (note linked to an entry)
  const handleAnnotationRequest = useCallback((entry) => {
    setAnnotationTarget(entry);
    setShowNoteModal(true);
  }, []);

  // Handle note/annotation submission
  // FT-284: forward timeOptions and bubble boundaryError back to the modal so
  // it can surface the banner and stay open.
  const handleNoteSubmit = useCallback(async (noteText, linkedEntryId, timeOptions) => {
    return await onLogNote?.(noteText, linkedEntryId, timeOptions);
  }, [onLogNote]);

  // Handle weather modal open
  const handleWeatherRequest = useCallback(() => {
    setShowWeatherModal(true);
  }, []);

  // Helper: Check if visibility value indicates restricted visibility
  const isRestrictedVisibility = useCallback((visibility) => {
    if (!visibility) return false;
    const restricted = ['fog', 'foggy', 'mist', 'restricted', 'poor'];
    return restricted.some(term => visibility.toLowerCase().includes(term));
  }, []);

  // Handle weather submission
  // FT-284: forward timeOptions; bubble boundaryError back so modal stays
  // open. Only run the restricted-visibility follow-up when the log actually
  // succeeded (no boundary error).
  const handleWeatherSubmit = useCallback(async (weatherData, timeOptions) => {
    const result = await onLogWeather?.(weatherData, timeOptions);
    if (result?.boundaryError) {
      return result;
    }

    // After successful weather log, check for restricted visibility
    if (
      isRestrictedVisibility(weatherData.visibility) &&
      !dismissedRestrictedVis &&
      voyage?.vesselConfig?.autoPromptRestrictedVisibility !== false
    ) {
      setRestrictedVisPrompt({
        isOpen: true,
        visibility: weatherData.visibility
      });
    }
  }, [onLogWeather, isRestrictedVisibility, dismissedRestrictedVis, voyage?.vesselConfig?.autoPromptRestrictedVisibility]);

  // Handle fuel modal open
  const handleFuelRequest = useCallback(() => {
    setShowFuelModal(true);
  }, []);

  // Handle fuel submission
  // FT-284: forward timeOptions; close-on-success owned by parent. Modal
  // surfaces boundary error via its local <ModalErrorBanner>.
  const handleFuelSubmit = useCallback(async (fuelData, timeOptions) => {
    const result = await onLogFuel?.(fuelData, timeOptions);
    if (result?.boundaryError) {
      return result;
    }
    setShowFuelModal(false);
  }, [onLogFuel]);

  // Handle position modal open
  const handlePositionRequest = useCallback(() => {
    setShowPositionModal(true);
  }, []);

  // Handle position submission (from modal - GPS or manual)
  // FT-284: forward timeOptions; close-on-success owned by modal (it calls
  // its own `onClose` after no-boundary-error). We propagate the result so
  // the modal can keep itself open on boundary error.
  const handlePositionSubmit = useCallback(async (positionData, timeOptions) => {
    if (!(positionData?.lat && positionData?.lon)) return;
    return await onLogPosition?.(positionData, timeOptions);
  }, [onLogPosition]);

  // Handle compass modal open
  const handleCompassRequest = useCallback(() => {
    setShowCompassModal(true);
  }, []);

  // Handle compass check submission - log as structured note
  const handleCompassSubmit = useCallback((compassData) => {
    // Format note based on mode/data received
    let noteText;

    if (compassData.status) {
      // Quick mode result
      if (compassData.status === 'ok') {
        noteText = `Compass check: OK (deviation ${compassData.deviationRange || '<3°'})`;
      } else {
        noteText = `Compass check: ERROR (deviation ${compassData.deviationRange || '>5°'}) - requires attention`;
      }
    } else if (compassData.heading !== undefined) {
      // Standard/Full mode with heading
      const devSign = compassData.deviation > 0 ? '+' : '';
      noteText = `Compass check: ${compassData.heading}° hdg, ${devSign}${compassData.deviation}° dev`;
      if (compassData.method) {
        noteText += ` (${compassData.method})`;
      }
      if (compassData.gyroHeading) {
        noteText += ` [Gyro: ${compassData.gyroHeading}°]`;
      }
    } else {
      // Fallback
      noteText = 'Compass check completed';
    }

    onLogNote?.(noteText);
    setShowCompassModal(false);
  }, [onLogNote]);

  // FT-80: Handle Daily Checks quick access request
  const handleDailyChecksRequest = useCallback(() => {
    setShowDailyChecks(true);
  }, []);

  // FT-80 / FT-261: Handle Daily Checks log entry (auto-logs when category complete).
  // FT-261 (2026-05-31): Removed the onLogNote?.(noteText) call. DailyChecksQuickAccess
  // already calls logDailyCheck() which writes a proper auto-logged entry to the
  // main logbook (type=daily_check, sourceModule=daily_checks, structured summary).
  // The voyage-tab path was additionally calling onLogNote here with a bare string,
  // which created a malformed second logbook entry (type=undefined, title='Entry',
  // sourceModule='unknown'). Observed at staging cloud entries [12]/[13] on
  // 2026-05-31T06:52:37.343Z + .344Z — 1ms apart, same daily-check completion.
  const handleDailyCheckLogEntry = useCallback(() => {
    setShowDailyChecks(false);
  }, []);

  // Handle restricted visibility checklist confirmation
  const handleRestrictedVisConfirm = useCallback(() => {
    setRestrictedVisPrompt({ isOpen: false, visibility: null });
    // Start the restricted visibility checklist operation
    onStartOperation?.('restricted_visibility', {
      triggeredBy: 'weather_log',
      visibility: restrictedVisPrompt.visibility
    });
  }, [onStartOperation, restrictedVisPrompt.visibility]);

  // Handle "Remind Later" - close but allow prompt again
  const handleRestrictedVisLater = useCallback(() => {
    setRestrictedVisPrompt({ isOpen: false, visibility: null });
    // Don't set dismissedRestrictedVis - allow prompt again on next weather log
  }, []);

  // Handle dismiss - don't prompt again this voyage
  const handleRestrictedVisDismiss = useCallback(() => {
    setRestrictedVisPrompt({ isOpen: false, visibility: null });
    setDismissedRestrictedVis(true); // Don't prompt again this voyage
  }, []);

  // ═══════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════

  return (
    <div className="flex flex-col h-full bg-slate-50">
      {/* Mobile: Floating dark header at top of screen */}
      <div className="sm:hidden">
        <MobileVoyageHeader
          voyage={voyage}
          onBack={onClose}
          onMenuClick={() => window.dispatchEvent(new CustomEvent('shell:openSidebar'))}
        />
      </div>

      {/* Tablet/Desktop: Standard VoyageHeader */}
      <div className="hidden sm:block">
        <VoyageHeader
          voyage={voyage}
          onEdit={onEditVoyage}
          onBack={onClose}
          isReadOnly={hasStarted}
        />
      </div>

      {/* Search and Filter Bar - only show if there are entries */}
      {sortedEntries.length > 0 && (
        <div className="bg-white border-b border-slate-200 px-3 py-2 space-y-2">
          {/* Search input */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search log entries..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-8 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Type filter tabs */}
          <div className="flex gap-1 overflow-x-auto pb-1 -mx-1 px-1 scrollbar-hide">
            {typeFilterOptions.map(option => (
              <button
                key={option.value}
                onClick={() => setTypeFilter(option.value)}
                className={`px-3 py-1 text-xs font-medium rounded-full whitespace-nowrap transition-colors ${
                  typeFilter === option.value
                    ? 'bg-cyan-600 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>

          {/* Results count when filtering */}
          {(searchTerm || typeFilter !== 'all') && (
            <div className="text-xs text-slate-500">
              Showing {filteredEntries.length} of {sortedEntries.length} entries
              {(searchTerm || typeFilter !== 'all') && (
                <button
                  onClick={() => { setSearchTerm(''); setTypeFilter('all'); }}
                  className="ml-2 text-cyan-600 hover:text-cyan-700"
                >
                  Clear filters
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Scrollable log content - pb-36 for all sizes to clear FloatingActionBar */}
      <div className="flex-1 overflow-y-auto pb-36">
        {/* Sub-ticket C: optional top-of-scroll slot for VoyagePlanUnderwayCard.
            Rendered ABOVE log entries (and the empty-state copy) so the Master
            sees the plan at the top of the viewport. Card-internal guards
            handle pre-B voyages / non-attached / empty-plan states. */}
        {topSlot}
        {sortedEntries.length === 0 ? (
          // Empty state - no entries at all
          <div className="p-8 text-center">
            <p className="text-slate-500">No log entries yet.</p>
            <p className="text-sm text-slate-400 mt-2">
              Complete pre-departure checks to begin the voyage.
            </p>
          </div>
        ) : processedEntries.length === 0 && !preDepCheckStatus.allCompleted ? (
          // No matching results state
          <div className="p-8 text-center">
            <p className="text-slate-500">No matching entries found.</p>
            <p className="text-sm text-slate-400 mt-2">
              Try adjusting your search or filters.
            </p>
            <button
              onClick={() => { setSearchTerm(''); setTypeFilter('all'); }}
              className="mt-3 text-sm text-cyan-600 hover:text-cyan-700 font-medium"
            >
              Clear all filters
            </button>
          </div>
        ) : (
          // Log entries
          <div className="divide-y divide-slate-100">
            {/* FT-161 Phase 2: Render compact pre-departure card at top when all checks completed */}
            {preDepCheckStatus.allCompleted && (typeFilter === 'all' || typeFilter === 'check') && (
              <PreDepartureCompactCard
                checks={preDepCheckStatus.checks}
                voyage={voyage}
                timestamp={preDepCheckStatus.checks[0]?.timestamp}
                onAnnotate={handleAnnotationRequest}
              />
            )}

            {/* Render remaining entries */}
            {processedEntries.map((entry, index) => {
              // FT-161 Phase 4: Render OperationCard for grouped operations
              if (entry.type === 'operation_card') {
                return (
                  <OperationCard
                    key={entry.id || `operation-${entry.operationId}`}
                    operationType={entry.operationType}
                    sectionEntry={entry.sectionEntry}
                    operationDataEntry={entry.operationDataEntry}
                    checks={entry.checks}
                    completeEntry={entry.completeEntry}
                    vesselTimezone={vesselTimezone}
                  />
                );
              }
              // Default: Render LogEntry
              return (
                <LogEntry
                  key={entry.id || `entry-${index}-${entry.timestamp}`}
                  entry={entry}
                  voyage={voyage}
                  onCheckComplete={handleCheckComplete}
                  onCheckSkip={handleCheckSkipRequest}
                  onAnnotate={handleAnnotationRequest}
                  isEditable={!hasEnded}
                  canModify={canModifyCheck(entry)}
                  currentPhase={currentPhase}
                  isLast={index === processedEntries.length - 1}
                />
              );
            })}

            {/* FT-283 §5.1: unified Complete operation button — sibling of
                processedEntries.map. Renders for UNDERWAY/ARRIVAL with an
                active op. Survives typeFilter !== 'all' (which would
                otherwise hide check entries). */}
            {activeOperation
              && (voyage?.status === VOYAGE_STATUS.UNDERWAY
                  || voyage?.status === VOYAGE_STATUS.ARRIVAL)
              && !hasEnded && (
              <CompleteOperationRow
                activeOperation={activeOperation}
                operationChecks={unifiedLog.filter(
                  e => e.type === 'check' && e.operationId === activeOperation.operationId
                )}
                voyage={voyage}
                currentVessel={currentVessel}
                onRequestComplete={handleCompleteOperationRequest}
              />
            )}
          </div>
        )}
      </div>

      {/* Floating action bar - 4 buttons on mobile with Log picker.
          FT-283: onCompleteOperation prop removed — Complete is now owned by
          <CompleteOperationRow> at the bottom of the active checklist. */}
      <FloatingActionBar
        onLogPosition={handlePositionRequest}
        onLogWeather={handleWeatherRequest}
        onLogNote={handleNoteRequest}
        onLogFuel={handleFuelRequest}
        onCompassCheck={handleCompassRequest}
        onStartOperation={handleOperationPickerRequest}
        onPrepareForArrival={onPrepareForArrival}
        onDailyChecks={handleDailyChecksRequest}
        hasActiveOperation={!!activeOperation}
        activeOperationType={activeOperation?.operationType}
        preArrivalDone={preArrivalDone}  // FT-285 Item 4: hard-lock Arrive
        isReadOnly={hasEnded}
        onMenuClick={() => window.dispatchEvent(new CustomEvent('shell:openSidebar'))}
        onBack={onClose}
      />

      {/* Skip check modal */}
      {skipModalData && (
        <SkipCheckModal
          check={skipModalData}
          onConfirm={handleSkipConfirm}
          onCancel={handleSkipCancel}
        />
      )}

      {/* Operation data modal */}
      {operationDataModal && (
        <OperationDataModal
          operationType={operationDataModal.operationType}
          onSubmit={handleOperationDataSubmit}
          onCancel={handleOperationDataCancel}
          vesselTimezone={vesselTimezone}
        />
      )}

      {/* Weather input modal */}
      <WeatherInputModal
        isOpen={showWeatherModal}
        onClose={() => setShowWeatherModal(false)}
        onSubmit={handleWeatherSubmit}
        vesselTimezone={vesselTimezone}
      />

      {/* Note input modal */}
      <NoteInputModal
        isOpen={showNoteModal}
        onClose={() => {
          setShowNoteModal(false);
          setAnnotationTarget(null);
        }}
        onSubmit={handleNoteSubmit}
        linkedEntry={annotationTarget}
        vesselTimezone={vesselTimezone}
      />

      {/* Fuel input modal */}
      {showFuelModal && (
        <FuelInputModal
          title="Log Fuel Reading"
          subtitle="Record current fuel level"
          onConfirm={handleFuelSubmit}
          onCancel={() => setShowFuelModal(false)}
          required={false}
          vesselTimezone={vesselTimezone}
        />
      )}

      {/* Position input modal */}
      {/* Sub-ticket C: onClose also clears planItemPositionContext so a
          subsequent FAB-Position tap is not wrongly tagged with the previous
          planItemId. Note: handlePositionSubmit (in UnderwayPhase) ALSO
          clears the context on a successful logPositionV2 — this onClose is
          the cancel-path counterpart. */}
      <PositionInputModal
        isOpen={showPositionModal}
        onClose={() => {
          setShowPositionModal(false);
          onClearPlanItemPositionContext?.();
        }}
        onSubmit={handlePositionSubmit}
        vesselTimezone={vesselTimezone}
      />

      {/* Compass check modal (3-mode: quick/standard/full) */}
      <CompassCheckModal
        isOpen={showCompassModal}
        onClose={() => setShowCompassModal(false)}
        onSubmit={handleCompassSubmit}
        mode="quick"  // TODO: Get from vessel settings based on vessel size
      />

      {/* Operation picker modal */}
      <OperationPickerModal
        isOpen={showOperationPicker}
        onClose={() => setShowOperationPicker(false)}
        onSelect={handleOperationSelect}
      />

      {/* FT-80: Daily Checks Quick Access - renders CategoryPicker when showDailyChecks is true */}
      {showDailyChecks && (
        <DailyChecksQuickAccess
          onLogEntry={handleDailyCheckLogEntry}
          autoOpen={true}
          onClose={() => setShowDailyChecks(false)}
        />
      )}

      {/* Restricted Visibility Prompt */}
      <RestrictedVisibilityPrompt
        isOpen={restrictedVisPrompt.isOpen}
        visibility={restrictedVisPrompt.visibility}
        onConfirm={handleRestrictedVisConfirm}
        onLater={handleRestrictedVisLater}
        onDismiss={handleRestrictedVisDismiss}
      />

      {/* FT-281: green "Complete Operation?" auto-confirm modal removed.
          TimePickerModal in UnderwayPhase serves as the confirmation. */}

      {/* FT-283: bulk-skip-reason modal. Mounted when CompleteOperationRow
          detected incomplete checks. Confirm fires onBulkSkipAndComplete with
          the typed reason; cancel releases the in-flight guard. */}
      <BulkSkipReasonModal
        isOpen={bulkSkipReasonOpen}
        incompleteChecks={bulkSkipPayload?.incomplete || []}
        onCancel={handleBulkSkipCancel}
        onConfirm={handleBulkSkipConfirm}
      />

      {/* FT-202: ALL FAST button - appears after pre_arrival operation is completed */}
      {preArrivalJustCompleted && !hasEnded && onAllFast && (
        <div className="fixed bottom-24 left-0 right-0 z-40 px-4 sm:px-6">
          <div className="max-w-2xl mx-auto">
            <button
              onClick={onAllFast}
              className="w-full bg-amber-600 hover:bg-amber-700 text-white font-semibold py-4 px-6 rounded-xl shadow-lg flex items-center justify-center gap-2 transition-all transform hover:scale-[1.02]"
            >
              <span className="text-lg">⚓</span>
              <span className="text-lg">ALL FAST - Complete Arrival</span>
            </button>
          </div>
        </div>
      )}

      {/* FT-202: ALL FAST confirmation modal.
          FT-285 Item 2: now hosts an inline event-time field via the optional
          timeField prop. The Master confirms the time + sign-off in one step;
          the standalone TimePickerModal mount in UnderwayPhase is gone. */}
      {showAllFastModal && onAllFastConfirm && onAllFastCancel && (
        <ConfirmSignOffModal
          isOpen={showAllFastModal}
          onClose={onAllFastCancel}
          onConfirm={onAllFastConfirm}
          title="ALL FAST"
          description="Confirm arrival and completion of voyage. This will create an ALL FAST milestone in the voyage log."
          timeField={allFastTimeField}
          vesselTimezone={vesselTimezone}
          timeFieldLabel="All Fast Time"
        />
      )}
    </div>
  );
};

export default VoyageDocument;
